import React, { useId, useMemo, useState } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Brush,
} from 'recharts';
import TimeframeSelector from './TimeframeSelector';

const PILL_DAYS = {
  '1M': 30, '3M': 90, '6M': 180, '1Y': 365,
  '2Y': 365 * 2, '3Y': 365 * 3, '5Y': 365 * 5, '7Y': 365 * 7, '10Y': 365 * 10,
};

export const DAILY_PRESETS = [
  { key: '1M', label: '1M' },
  { key: '3M', label: '3M' },
  { key: '6M', label: '6M' },
  { key: 'YTD', label: 'YTD' },
  { key: '1Y', label: '1Y' },
  { key: '2Y', label: '2Y' },
  { key: '5Y', label: '5Y' },
  { key: '10Y', label: '10Y' },
  { key: 'ALL', label: 'ALL' },
];

export const ECON_PRESETS = [
  { key: '1Y', label: '1Y' },
  { key: '3Y', label: '3Y' },
  { key: '5Y', label: '5Y' },
  { key: '10Y', label: '10Y' },
  { key: 'ALL', label: 'MAX' },
];

const isoToday = () => new Date().toISOString().slice(0, 10);
const isoNDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const isoYtdStart = () => {
  const d = new Date();
  return new Date(d.getFullYear(), 0, 1).toISOString().slice(0, 10);
};

// Resolve a pill key to {start, end} dates, given the reference series for ALL.
function pillToRange(pill, referenceSeries) {
  if (pill === 'ALL') {
    return { start: referenceSeries?.[0]?.date || isoNDaysAgo(365), end: isoToday() };
  }
  if (pill === 'YTD') {
    return { start: isoYtdStart(), end: isoToday() };
  }
  const days = PILL_DAYS[pill];
  if (!days) return { start: isoNDaysAgo(365), end: isoToday() };
  return { start: isoNDaysAgo(days), end: isoToday() };
}

// Index lookup with binary search (sorted-by-date series can be huge).
function findStartIndex(series, isoDate) {
  if (!series?.length) return 0;
  let lo = 0;
  let hi = series.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (series[mid].date < isoDate) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}
function findEndIndex(series, isoDate) {
  if (!series?.length) return 0;
  let lo = 0;
  let hi = series.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (series[mid].date > isoDate) hi = mid - 1;
    else lo = mid;
  }
  return lo;
}

// Snap a desired {start, end} to the actual dates present in the series, so the
// brush's controlled indices line up exactly with real bars (the right handle
// lands on the last available trading day rather than a calendar "today" that
// has no bar). Keeps Recharts' controlled-index sync honest.
function snapToSeries(series, start, end) {
  if (!series?.length) return { start, end };
  const s = findStartIndex(series, start);
  const e = findEndIndex(series, end);
  return { start: series[s].date, end: series[e].date };
}

function RangeControl({
  referenceSeries,
  dataKey = 'close',
  value,
  onChange,
  presets = DAILY_PRESETS,
  defaultPreset = '1Y',
  title = 'Date range',
  caption = '',
  unitLabel = 'observations',
  referenceLabel = 'drag the handles or click a preset above',
  yTickFormatter,
}) {
  const gradientId = `rangeRefGrad-${useId()}`;
  const [activePill, setActivePill] = useState(defaultPreset);
  const [selectedYear, setSelectedYear] = useState('');
  // Bumped only on programmatic range changes (preset/year) to remount the
  // Brush so its store-backed travellers re-sync to the new controlled
  // indices. Never bumped on user drag, so dragging stays smooth.
  const [syncNonce, setSyncNonce] = useState(0);

  const { startIndex, endIndex } = useMemo(() => {
    if (!referenceSeries?.length || !value?.start || !value?.end) {
      return { startIndex: 0, endIndex: (referenceSeries?.length || 1) - 1 };
    }
    return {
      startIndex: findStartIndex(referenceSeries, value.start),
      endIndex: findEndIndex(referenceSeries, value.end),
    };
  }, [referenceSeries, value]);

  // Last 10 calendar years present in the reference series (newest first).
  const years = useMemo(() => {
    if (!referenceSeries?.length) return [];
    const latest = parseInt(referenceSeries[referenceSeries.length - 1].date.slice(0, 4), 10);
    const earliest = parseInt(referenceSeries[0].date.slice(0, 4), 10);
    const out = [];
    for (let y = latest; y > latest - 10 && y >= earliest; y -= 1) out.push(y);
    return out;
  }, [referenceSeries]);

  const applyPill = (pill) => {
    setActivePill(pill);
    setSelectedYear('');
    setSyncNonce((n) => n + 1);
    const { start, end } = pillToRange(pill, referenceSeries);
    onChange(snapToSeries(referenceSeries, start, end));
  };

  const applyYear = (yearStr) => {
    setSelectedYear(yearStr);
    if (!yearStr) return;
    setActivePill(null);
    setSyncNonce((n) => n + 1);
    onChange(snapToSeries(referenceSeries, `${yearStr}-01-01`, `${yearStr}-12-31`));
  };

  const handleBrushChange = ({ startIndex: s, endIndex: e } = {}) => {
    if (s == null || e == null || !referenceSeries?.[s] || !referenceSeries?.[e]) return;
    const next = { start: referenceSeries[s].date, end: referenceSeries[e].date };
    // Suppress no-op fires (Recharts can re-emit when props change)
    if (next.start === value?.start && next.end === value?.end) return;
    setActivePill(null);
    setSelectedYear('');
    onChange(next);
  };

  const span = endIndex - startIndex + 1;
  const defaultYFmt = (v) => (typeof v === 'number' ? Math.round(v).toString() : v);

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-gray-300">{title}</div>
          <div className="text-xs text-gray-500">
            {caption && `${caption} · `}
            From {value?.start || '—'} → {value?.end || '—'} · {span} {unitLabel}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <TimeframeSelector
            options={presets}
            value={activePill || ''}
            onChange={applyPill}
          />
          {years.length > 0 && (
            <select
              value={selectedYear}
              onChange={(e) => applyYear(e.target.value)}
              className="bg-gray-800 border border-gray-600 rounded-md text-xs text-gray-200 px-2 py-1"
              aria-label="Jump to year"
            >
              <option value="">Jump to year…</option>
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          )}
        </div>
      </div>
      <div className="min-h-[130px]">
        <ResponsiveContainer width="100%" height={130}>
          <AreaChart data={referenceSeries || []} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#60a5fa" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#60a5fa" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 9 }} minTickGap={80} />
            <YAxis
              tick={{ fill: '#9ca3af', fontSize: 9 }}
              domain={['dataMin', 'dataMax']}
              width={45}
              tickFormatter={yTickFormatter || defaultYFmt}
            />
            <Area type="monotone" dataKey={dataKey} stroke="#60a5fa" fill={`url(#${gradientId})`} strokeWidth={1} />
            <Brush
              key={syncNonce}
              dataKey="date"
              startIndex={startIndex}
              endIndex={endIndex}
              onChange={handleBrushChange}
              height={26}
              stroke="#60a5fa"
              fill="#1f2937"
              travellerWidth={8}
              tickFormatter={() => ''}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="text-[10px] text-gray-500 mt-1">{referenceLabel}</div>
    </div>
  );
}

export default RangeControl;
